import { _ } from "../../core/polyglot/polyglot.js";
import { widget } from "@foretagsplatsen/widgetjs";
import keyFigureValueFormatter from "../../models/keyFigure/rendering/keyFigureValueFormatter.js";
import popoverWidget from "../../core/widgets/popoverWidget/popoverWidget.js";
import { arrowDown, arrowUp } from "../../core/elements/icons/index.js";

/**
 * Displays a single Keyfigure value for the keyFigureOverview. A Keyfigure can be clicked
 * to get a description and a link to the Keyfigure document.
 *
 * @param {{}} spec - widget spec - Parameter object
 * @param {keyFigureValue} spec.keyFigureValue - keyFigureValue
 * @param {boolean} [spec.canViewAccountingDocuments] - If false, links to KPI documents are not displayed.
 * @param {boolean} [spec.canViewKeyFigure] - If false, cta to view the KPI is disabled
 * @param {renderable} [spec.navigateToViewKeyFigureDisableMessage] - Message to show when the KPI call to action is disabled
 * @param {{}} my - protected fields holder
 * @return {*}
 */
function keyFigureValueWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	const keyFigureValue = spec.keyFigureValue;
	const canViewAccountingDocuments =
		spec.canViewAccountingDocuments !== false;
	const canViewKeyFigure = spec.canViewKeyFigure;
	const navigateToViewKeyFigureDisableMessage =
		spec.navigateToViewKeyFigureDisableMessage;

	const popover = popoverWidget({
		header: function (html) {
			html.span(
				html.strong(keyFigureValue.getName()),
				" ",
			).renderNonBreaking(keyFigureValueFormatter.format(keyFigureValue));
		},
		content: function (html) {
			html.render(keyFigureValue.getComponent().getDescription());
		},
		primaryLabel: canViewAccountingDocuments && _("View KPI"),
		primaryAction: function () {
			my.redirectTo("single-kpi", {
				keyFigure: keyFigureValue.getComponent().getId(),
			});
		},
		isPrimaryActionEnabled: canViewKeyFigure,
		primaryActionDisabledMessage: navigateToViewKeyFigureDisableMessage,
	});

	that.renderOn = function (html) {
		html.div({ id: that.getId(), class: "keyfigure-wrapper" }, (html) => {
			const div = html.div(
				{
					id: `kf-${keyFigureValue.getComponent().getId()}`,
					class: "keyfigure-container no-target clickable",
					"data-test": "keyfigure-value-container",
				},
				my.nameRenderer(),
				my.valueRenderer(),
			);

			popover.setTriggerItem(div);
		});
	};

	my.nameRenderer = function () {
		return (html) => {
			html.h2(
				{ "data-test": "keyfigure-name", class: "keyfigure-label" },
				keyFigureValue.getName(),
				my.arrowRenderer(),
			);
		};
	};

	my.arrowRenderer = function () {
		const shouldShowArrow =
			keyFigureValue.isPercentage() && keyFigureValue.getValue();
		if (!shouldShowArrow) return () => {};

		const negative = keyFigureValue.getValue() < 0;

		return (html) =>
			html.div(
				{
					class: { negative, positive: !negative },
					"data-test": "kpi-evolution",
				},
				negative ? arrowDown : arrowUp,
			);
	};

	my.valueRenderer = function () {
		return (html) => {
			const value = keyFigureValue.getValue();
			const isNull = value === null;

			html.h1(
				{
					"data-test": "keyfigure-value",
					class: { negative: value < 0, neutral: isNull },
				},
				isNull ? "-" : keyFigureValueFormatter.format(keyFigureValue),
			);
		};
	};

	return that;
}

export default keyFigureValueWidget;
